import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";

const WHATSAPP_NUMBER = "15304444477"; // no + or dashes for wa.me
const PREFILL = encodeURIComponent(
  "Hi Krishna, I want to take my business to the next level but I'm not sure where to begin. Can we connect for a diagnosis and work together?"
);
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${PREFILL}`;

export default function FloatingContact() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Delay the bubble entrance so it doesn't distract on first load
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 2200);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-50 flex flex-col items-end gap-3" data-testid="floating-contact">
      <AnimatePresence>
        {visible && expanded && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.94 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-72 rounded-2xl border border-line bg-bg shadow-[0_20px_60px_-15px_rgba(28,28,28,0.25)] p-5"
            data-testid="floating-contact-card"
          >
            <button
              onClick={() => setExpanded(false)}
              className="absolute top-3 right-3 text-ink-muted hover:text-ink transition-colors"
              aria-label="Close"
              data-testid="floating-contact-close"
            >
              <X className="h-4 w-4" />
            </button>
            <p className="font-sans text-xs uppercase tracking-mega text-brass mb-2">A word</p>
            <p className="font-serif text-xl text-ink leading-snug">
              Rather talk it through?
            </p>
            <p className="mt-2 font-sans text-sm text-ink-muted leading-relaxed">
              Send me a message on WhatsApp and I’ll read it. No forms, no gatekeeping.
            </p>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="floating-contact-whatsapp"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-sans text-xs uppercase tracking-wider font-semibold text-bg hover:bg-forest-hover transition-colors"
              onClick={() => setExpanded(false)}
            >
              Open WhatsApp
              <MessageCircle className="h-4 w-4" />
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {visible && (
          <motion.button
            initial={{ opacity: 0, scale: 0.6, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onClick={() => setExpanded((e) => !e)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative flex h-14 w-14 items-center justify-center rounded-full bg-forest text-bg shadow-[0_14px_40px_-10px_rgba(43,74,59,0.55)] hover:bg-forest-hover transition-colors"
            aria-label="Contact Krishna on WhatsApp"
            data-testid="floating-contact-toggle"
          >
            {/* pulsing ring */}
            <span className="pointer-events-none absolute inset-0 rounded-full bg-forest opacity-40 animate-ping" style={{ animationDuration: "2.4s" }} />
            <MessageCircle className="relative h-6 w-6" strokeWidth={1.8} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
